import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { LocateFixed, MapPin, X } from "lucide-react";
import "./styles/PlaceSearch.css";
import { GenericValues } from "../../../constants/Enums";
import {
  loadGoogleMapsAPI,
  createSessionToken,
  fetchPlaceSuggestions,
  reverseGeocode,
} from "./helpers/PlacesSearchHelper";
import {
  setUserLocation,
  setUserLocationName,
  setSearchRadius,
} from "../../../dataManagement/uiDb/GeneralDb";
import genericUtility from "../../../services/utility/GenericUtility";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../dataManagement/uiDb/UiDb";
import { UserLocation } from "../../../interfaces/UserObject";
import { updateUserPreferences } from "../../../services/thunks/ProfileThunk";
import UserPreferencesUtility from "../../../services/utility/UserPreferencesUtility";
import { useNotification } from "../contexts/NotificationContext";
import { NotificationType } from "../../../constants/Enums";

interface Suggestion {
  text: string;
  place: google.maps.places.Place;
}

interface PlaceSearchProps {
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** @deprecated Use onClose instead */
  closeLocationPopup?: () => void;
  /** Modal mode: renders as a portal with overlay */
  isModal?: boolean;
  /** Controls modal visibility (only used when isModal=true) */
  isOpen?: boolean;
  /** Called when the modal should close */
  onClose?: () => void;
  /** When true (modal mode), hides close button and blocks backdrop dismiss */
  mandatory?: boolean;
  /** Whether to update the app's global location state (Redux, localStorage) */
  updateGlobalState?: boolean;
  /** Callback fired when a place is successfully selected */
  onPlaceSelected?: (location: UserLocation | null, radius?: number) => void;
}

const PlaceSearch: React.FC<PlaceSearchProps> = ({
  defaultValue = "",
  onChange,
  closeLocationPopup,
  isModal = false,
  isOpen = true,
  onClose,
  mandatory = false,
  updateGlobalState = true,
  onPlaceSelected,
}) => {
  // Unified close handler
  const handleClose = () => {
    onClose?.();
    closeLocationPopup?.();
  };
  const [query, setQuery] = useState("");
  const [isCurrentLocation, setIsCurrentLocation] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [apiLoaded, setApiLoaded] = useState(!!window.google?.maps);
  const userPreferences = useSelector((state: RootState) => state.profile.userPreferences);
  const generalDbLocation = useSelector((state: RootState) => state.general.UserLocation);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const suppressNextFetchRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const dispatch: AppDispatch = useDispatch();
  const { triggerNotification, startLoading, stopLoading } = useNotification();

  useEffect(() => {
    // A closed modal (e.g. App's always-mounted mandatory location picker)
    // must not pull in Google Maps on every page -- load it on first open.
    if (isModal && !isOpen) return;
    if (!apiLoaded) {
      loadGoogleMapsAPI(apiKey, () => setApiLoaded(true));
    }
  }, [apiKey, apiLoaded, isModal, isOpen]);

  useEffect(() => {
    if (!apiLoaded) return;
    const loadLibs = async () => {
      await google.maps.importLibrary("places");
      await google.maps.importLibrary("geometry");
    };

    loadLibs();
  }, [apiLoaded]);

  useEffect(() => {
    if (isModal && isOpen) {
      document.body.style.overflow = "hidden";
      // Reset search state when modal opens
      setQuery("");
      setSuggestions([]);
      setDropdownOpen(false);
      setIsCurrentLocation(false);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModal, isOpen]);

  useEffect(() => {
    onChange?.(query);
  }, [query]);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setDropdownOpen(false);
        setSuggestions([]);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!apiLoaded || !input.trim() || isCurrentLocation) return;
      setLoading(true);
      try {
        sessionTokenRef.current =
          sessionTokenRef.current || createSessionToken();
        const parsed = await fetchPlaceSuggestions(
          input,
          sessionTokenRef.current
        );

        setSuggestions(parsed.slice(0, 3));
      } catch (err) {
        console.error("Autocomplete fetch failed:", err);
      } finally {
        setLoading(false);
      }
    },
    [apiLoaded, isCurrentLocation]
  );

  function getPlaceRadius(viewport): number {
    if (!viewport) return 1000;

    const ne = viewport.getNorthEast();
    const sw = viewport.getSouthWest();

    // diagonal distance between NE and SW
    const diagonal = google.maps.geometry.spherical.computeDistanceBetween(
      ne,
      sw
    );

    // radius = half the diagonal, floored
    return Math.floor(diagonal / 2);
  }

  useEffect(() => {
    if (!dropdownOpen) return;
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false;
      return;
    }
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(() => fetchSuggestions(query), 350);
    return () => clearTimeout(timer);
  }, [query, dropdownOpen, fetchSuggestions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsCurrentLocation(false);
    if(e.target.value != ""){
    
    setDropdownOpen(true);
    }else{
      setDropdownOpen(false);
    }
  };

  const handleClearInput = () => {
    setQuery("");
    setSuggestions([]);
    setDropdownOpen(false);
    setIsCurrentLocation(false);
    sessionTokenRef.current = null;
  };

  const handleSelect = async (suggestion: Suggestion) => {
    try {
      await suggestion.place.fetchFields({
        fields: [
          "id",
          "displayName",
          "formattedAddress",
          "location",
          "viewport",
        ],
      });

      const placeName = suggestion.place.displayName?.toString() || "";
      const placeAddr = suggestion.place.formattedAddress || "";

      const location = suggestion.place.location;
      const viewport = suggestion.place.viewport;
      const placeLocation: UserLocation | null = location
        ? { userLatitude: location.lat(), userLongitude: location.lng(), userLocationDisplayName: `${placeName}${placeAddr ? " — " + placeAddr : ""}` }
        : null;
      const placeRadius = getPlaceRadius(viewport);

      suppressNextFetchRef.current = true;
      setQuery(`${placeName}${placeAddr ? " — " + placeAddr : ""}`);
      setIsCurrentLocation(false);
      setDropdownOpen(false);
      setSuggestions([]);

      if (updateGlobalState) {
        dispatch(setUserLocation(placeLocation));
        UserPreferencesUtility.updateUserLocation(userPreferences, placeLocation, dispatch);
        dispatch(setUserLocationName(`${placeName}${placeAddr ? " — " + placeAddr : ""}`));
        dispatch(setSearchRadius(placeRadius));

        // Persist to localStorage for cross-tab / refresh survival
        if (placeLocation) {
          genericUtility.setStoredLocation(placeLocation);
        }
      }

      onPlaceSelected?.(placeLocation, placeRadius);
      sessionTokenRef.current = null;
      handleClose();
    } catch (error) {
      console.error("Failed to fetch place details:", error);
    }
  };

  const handleUseCurrentLocation = async () => {
    setLoading(true);
    startLoading("Detecting your location...");
    try {
      const currentLocation = await genericUtility.getCurrentUserLocation();

      // Validate coordinates before using them
      if (!currentLocation ||
          typeof currentLocation.latitude !== 'number' ||
          typeof currentLocation.longitude !== 'number' ||
          isNaN(currentLocation.latitude) ||
          isNaN(currentLocation.longitude)) {
        throw new Error("Invalid location data received");
      }

      // Reverse geocode to get a human-readable area name
      let locationDisplayName: string = GenericValues.CURRENT_LOCATION;
      let formattedAddress = "";
      try {
        const geocoded = await reverseGeocode(
          currentLocation.latitude,
          currentLocation.longitude,
        );
        locationDisplayName = geocoded.displayName;
        formattedAddress = geocoded.formattedAddress;
      } catch (geocodeError) {
        // Non-fatal: fall back to generic "Current location" string
        console.warn("Reverse geocoding failed, using fallback:", geocodeError);
      }

      // Build a proper UserLocation object (matching handleSelect pattern)
      const userLocation: UserLocation = {
        userLatitude: currentLocation.latitude,
        userLongitude: currentLocation.longitude,
        userLocationDisplayName: locationDisplayName,
      };

      const displayText = formattedAddress
        ? `${locationDisplayName} — ${formattedAddress}`
        : locationDisplayName;

      suppressNextFetchRef.current = true;
      setQuery(displayText);
      setIsCurrentLocation(true);
      setDropdownOpen(false);
      setSuggestions([]);

      if (updateGlobalState) {
        // Dispatch to Redux (same pattern as handleSelect)
        dispatch(setUserLocation(userLocation));
        dispatch(setUserLocationName(displayText));

        // Sync to backend preferences (was previously missing for current location)
        UserPreferencesUtility.updateUserLocation(userPreferences, userLocation, dispatch);

        // Persist to localStorage for cross-tab / refresh survival
        genericUtility.setStoredLocation({
          userLatitude: currentLocation.latitude,
          userLongitude: currentLocation.longitude,
          userLocationDisplayName: locationDisplayName,
        });
      }

      onPlaceSelected?.(userLocation);
      handleClose();
    } catch (error) {
      console.error('Location error:', error);

      // getCurrentUserLocation already returns specific, actionable strings
      // (e.g. how to re-enable a blocked permission), so surface those directly
      // rather than re-deriving a plainer message.
      const errorMessage =
        typeof error === 'string' && error
          ? error
          : "Unable to retrieve your location";

      triggerNotification(errorMessage, NotificationType.ERROR);
    } finally {
      // Always clear loading state, even if error occurs
      setLoading(false);
      stopLoading();
    }
  };

  // Display the saved location using the utility fallback chain
  const resolvedLocation = genericUtility.resolveUserLocation(userPreferences, generalDbLocation);
  const currentLocationDisplay = genericUtility.getDisplayLocationName(resolvedLocation, false)
    || defaultValue || "No location selected";

  const searchContent = (
    <div className="ps-container" ref={containerRef}>
      {/* Search Input Section */}
      <div className="ps-search-section">
        <div className={`ps-search-wrapper ${query ? "has-value" : ""}`}>
          <svg
            className="ps-search-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => query && setDropdownOpen(true)}
            placeholder="Search for a city or area..."
            className="ps-search-input"
            readOnly={isCurrentLocation}
          />
          {query && (
            <button
              className="ps-clear-btn"
              onClick={handleClearInput}
              onMouseDown={(e) => e.preventDefault()}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Suggestions / Recent Searches - Attached to Input */}
        {dropdownOpen && (
          <div className="ps-suggestions-dropdown">
            <span className="ps-dropdown-label">Search Results</span>
            <ul className="ps-suggestions-list">
              {suggestions.length > 0
                ? suggestions.map((s, index) => (
                    <li
                      key={index}
                      onClick={() => handleSelect(s)}
                      onMouseDown={(e) => e.preventDefault()}
                      className="ps-suggestion-item"
                    >
                      <div className="ps-suggestion-icon">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                      </div>
                      <div className="ps-suggestion-text">
                        <span className="ps-suggestion-primary">{s.text}</span>
                      </div>
                    </li>
                  ))
                : !loading &&
                  query.trim() !== "" &&
                  !isCurrentLocation && (
                    <>
                      {[1, 2, 3].map((i) => (
                        <li key={i} className="ps-skeleton-item">
                          <div className="ps-skeleton-shimmer"></div>
                        </li>
                      ))}
                    </>
                  )}
            </ul>
          </div>
        )}
      </div>
      
      {/* Currently Selected Card */}
      <div className="ps-current-section">
        <span className="ps-section-label">Currently Selected</span>
        <button 
          className="ps-current-card"
          onClick={() => searchInputRef.current?.focus()}
          type="button"
        >
          <div className="ps-current-icon">
            <MapPin size={20} />
          </div>
          <span className="ps-current-text">{currentLocationDisplay}</span>
        </button>
      </div>

      {/* GPS Quick Action */}
      <div className="ps-gps-section">
        <button
          className="ps-gps-button"
          onClick={() => handleUseCurrentLocation()}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="ps-gps-icon">
            <LocateFixed size={20} />
          </div>
          <div className="ps-gps-text">
            <span className="ps-gps-primary">Use Current Location</span>
            <span className="ps-gps-secondary">Using GPS</span>
          </div>
        </button>
      </div>
    </div>
  );

  // Modal mode: wrap in portal with overlay
  if (isModal) {
    if (!isOpen) return null;

    return createPortal(
      <div className="ps-modal-overlay" onClick={mandatory ? undefined : handleClose}>
        <div
          className="ps-modal-container"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ps-modal-header">
            <div>
              <h2 className="ps-modal-title">Select Location</h2>
              {mandatory && (
                <p className="ps-modal-hint">Please select a location to continue</p>
              )}
            </div>
            {!mandatory && (
              <button className="ps-modal-close" onClick={handleClose}>
                <X size={20} />
              </button>
            )}
          </div>
          <div className="ps-modal-body">{searchContent}</div>
        </div>
      </div>,
      document.body
    );
  }

  // Inline mode: return content directly
  return searchContent;
};

export default PlaceSearch;
